import { useSuspenseQuery } from '@tanstack/react-query';
import { FlatfileRecord } from '@flatfile/hooks';
import { fetchCustomFields } from 'api/customFields';
import {
    CHECKBOX_DESCRIPTION,
    FIELD_TYPE,
    SOCIAL_ACCOUNT_DESCRIPTION,
    customFieldsToFlatfileFields,
    submitAction,
} from 'components/imports/blueprintUtils';
import {
    MAX_STR_LENGTH,
    base,
    boolean,
    customFieldSchema,
    fixAddress,
    listInvalidItems,
    nullable,
    required,
    socialAccount,
} from 'components/imports/schemaUtils';
import { ImportStrategy } from 'components/imports/types';
import { IMPORT_CONTACTS_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { yup } from 'helpers/yup';
import { customFieldQueryKeys } from 'queries/custom-field-query-keys';
import { Schema, ValidationError } from 'yup';

const companySchema = yup.object().shape({
    company: required.label('Company').max(MAX_STR_LENGTH),
    primary_email: nullable.label('Primary Email').email(),
    primary_phone: nullable.max(MAX_STR_LENGTH),
    emails: nullable.label('Email Addresses').test(
        'emails',
        ({ value }) => {
            const itemsList = listInvalidItems(item => !yup.string().email().isValidSync(item));

            return `Contains invalid email addresses: ${itemsList(value)}.`;
        },
        emails =>
            !emails ||
            emails
                .split(',')
                .map(i => i.trim())
                .every(e => yup.string().email().isValidSync(e)),
    ),
    address_1: base
        .label('Address Line 1')
        .max(MAX_STR_LENGTH)
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    address_2: nullable.label('Address Line 2').max(MAX_STR_LENGTH),
    city: base
        .label('City')
        .max(MAX_STR_LENGTH)
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    state: base
        .label('State')
        .max(MAX_STR_LENGTH)
        .state()
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    zipcode: base
        .label('Postal Code')
        .max(MAX_STR_LENGTH)
        .postalCode()
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    country: base
        .label('Country')
        .max(MAX_STR_LENGTH)
        .country()
        .when('$hasPartialAddress', ([hasPartialAddress], schema) =>
            hasPartialAddress
                ? schema.required('${label} is required for a valid address.')
                : schema.nullable(),
        ),
    tags: nullable.label('Tags').test(
        'tags',
        ({ value }: { value: string }) => {
            const itemsList = listInvalidItems(
                item => !yup.string().max(MAX_STR_LENGTH).isValidSync(item),
            );

            return `Contains tags over ${MAX_STR_LENGTH} characters: ${itemsList(value)}`;
        },
        tags =>
            !tags ||
            tags.split(',').every(t => yup.string().max(MAX_STR_LENGTH).isValidSync(t.trim())),
    ),
    twitter_url: socialAccount.label('Twitter URL'),
    linkedin_url: socialAccount.label('LinkedIn URL'),
    facebook_url: socialAccount.label('Facebook URL'),
    notes: nullable.label('Notes').max(MAX_STR_LENGTH),
    email_subscription: boolean
        .label('Email Subscription Status')
        .when('$hasEmails', ([hasEmails], schema) =>
            hasEmails
                ? schema.required('${label} is required when email is provided.')
                : schema.nullable(),
        ),
    phone_subscription: boolean.nullable().label('Phone Subscription Status'),
    company_point_of_contact_id: nullable
        .label('Point of Contact ID')
        .asNumber(schema => schema.nullable().positive()),
    point_of_contact_first_name: base
        .label('Point of Contact First Name')
        .max(MAX_STR_LENGTH)
        .when(
            ['$hasPointOfContactId', '$hasAnyRequiredPointOfContactFields'],
            ([hasPointOfContactId, hasAnyRequiredPointOfContactFields], schema) => {
                if (hasPointOfContactId) {
                    return schema.nullable();
                }

                return hasAnyRequiredPointOfContactFields
                    ? schema.required('${label} is required for a valid Point of Contact.')
                    : schema.nullable();
            },
        ),
    point_of_contact_last_name: base
        .label('Point of Contact Last Name')
        .max(MAX_STR_LENGTH)
        .when(
            ['$hasPointOfContactId', '$hasAnyRequiredPointOfContactFields'],
            ([hasPointOfContactId, hasAnyRequiredPointOfContactFields], schema) => {
                if (hasPointOfContactId) {
                    return schema.nullable();
                }

                return hasAnyRequiredPointOfContactFields
                    ? schema.required('${label} is required for a valid Point of Contact.')
                    : schema.nullable();
            },
        ),
    point_of_contact_primary_email: base
        .label('Point of Contact Primary Email')
        .email()
        .when(
            ['$hasPointOfContactId', '$hasAnyRequiredPointOfContactFields'],
            ([hasPointOfContactId, hasAnyRequiredPointOfContactFields], schema) => {
                if (hasPointOfContactId) {
                    return schema.nullable();
                }

                return hasAnyRequiredPointOfContactFields
                    ? schema.required('${label} is required for a valid Point of Contact.')
                    : schema.nullable();
            },
        ),
    point_of_contact_primary_phone: nullable
        .label('Point of Contact Primary Phone')
        .max(MAX_STR_LENGTH)
        .when(
            ['$hasPointOfContactId', '$hasAnyRequiredPointOfContactFields'],
            ([hasPointOfContactId, hasAnyRequiredPointOfContactFields], schema) => {
                if (!hasPointOfContactId && !hasAnyRequiredPointOfContactFields) {
                    return schema.test(
                        'phone_cannot_have_value_when_point_of_contact_fields_are_empty',
                        ({ label }) =>
                            `${label} cannot have a value when other Point of Contact fields are empty.`,
                        value => {
                            return value === null;
                        },
                    );
                }

                return schema;
            },
        ),
});

const useCompanyImportStrategy = (): ImportStrategy => {
    const { accountId } = useCurrentAccount();
    const params = { group: 'contact' as const, limit: 9999 };

    const { data: companyCustomFields } = useSuspenseQuery({
        queryKey: customFieldQueryKeys.customFieldsWithFilters(accountId, params),
        queryFn: () => fetchCustomFields(accountId, params),
    });
    const customFields = companyCustomFields?.data ?? [];

    const schema = companySchema.concat(
        yup.object().shape(
            customFields.reduce(
                (acc, field) => {
                    acc[`custom_fields_field_${field.id}`] = customFieldSchema(field).label(
                        field.title,
                    );

                    return acc;
                },
                {} as Record<string, Schema>,
            ),
        ),
    );

    return {
        displayName: 'companies',
        type: 'companies',
        hasDownloadTemplate: true,
        helpCenterUrl: IMPORT_CONTACTS_LINK,
        blueprint: {
            name: 'Import companies',
            sheets: [
                {
                    name: 'Companies',
                    slug: 'companies',
                    fields: [
                        {
                            label: 'Company',
                            key: 'company_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Primary Email',
                            key: 'primary_email',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Email Addresses',
                            key: 'emails',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Primary Phone Number',
                            key: 'primary_phone',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Phone Numbers',
                            key: 'phones',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Address Line 1',
                            key: 'address_1',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Address Line 2',
                            key: 'address_2',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'City',
                            key: 'city',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'State',
                            key: 'state',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Zip Code',
                            key: 'zipcode',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Country',
                            key: 'country',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Tags',
                            key: 'tags',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Twitter URL',
                            key: 'twitter_url',
                            type: FIELD_TYPE.string,
                            description: SOCIAL_ACCOUNT_DESCRIPTION,
                        },
                        {
                            label: 'LinkedIn URL',
                            key: 'linkedin_url',
                            type: FIELD_TYPE.string,
                            description: SOCIAL_ACCOUNT_DESCRIPTION,
                        },
                        {
                            label: 'Facebook URL',
                            key: 'facebook_url',
                            type: FIELD_TYPE.string,
                            description: SOCIAL_ACCOUNT_DESCRIPTION,
                        },
                        {
                            label: 'Notes',
                            key: 'note',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Email Subscription Status',
                            key: 'email_subscription',
                            type: FIELD_TYPE.string,
                            description: CHECKBOX_DESCRIPTION,
                        },
                        {
                            label: 'Phone Subscription Status',
                            key: 'phone_subscription',
                            type: FIELD_TYPE.string,
                            description: CHECKBOX_DESCRIPTION,
                        },
                        {
                            label: 'Point of Contact ID',
                            key: 'company_point_of_contact_id',
                            type: FIELD_TYPE.number,
                        },
                        {
                            label: 'Point of Contact First Name',
                            key: 'point_of_contact_first_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Point of Contact Last Name',
                            key: 'point_of_contact_last_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Point of Contact Primary Email',
                            key: 'point_of_contact_primary_email',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Point of Contact Primary Phone',
                            key: 'point_of_contact_primary_phone',
                            type: FIELD_TYPE.string,
                        },
                        ...(customFields.length > 0
                            ? customFieldsToFlatfileFields(customFields)
                            : []),
                    ],
                },
            ],
            actions: [submitAction],
        },
        recordHook: (record: FlatfileRecord) => {
            const data = record.value;

            fixAddress(record);

            const context = {
                hasPartialAddress:
                    data.address_1 !== null ||
                    data.address_2 !== null ||
                    data.city !== null ||
                    data.state !== null ||
                    data.zipcode !== null ||
                    data.country !== null,
                hasEmails: data.emails !== null || data.primary_email !== null,
                hasPointOfContactId: data.company_point_of_contact_id !== null,
                hasAnyRequiredPointOfContactFields:
                    data.point_of_contact_first_name !== null ||
                    data.point_of_contact_last_name !== null ||
                    data.point_of_contact_primary_email !== null,
            };

            try {
                schema.validateSync(record.value, { abortEarly: false, context });
            } catch (err) {
                if (!(err instanceof ValidationError)) {
                    return record;
                }

                if (!err.inner) {
                    record.addError(err.path!, err.message);

                    return record;
                }

                err.inner.forEach((e: any) => {
                    record.addError(e.path, e.message);
                });
            }

            return record;
        },
    };
};

export { useCompanyImportStrategy };
